import { useEffect, useState } from "react";
import useAnalyticsService from "../../../services/AnalyticsService";
import { useUrlDateRange } from "../../../hooks/useUrlDateRange";
import DateRangeToolbar from "../../../components/DateRangeToolbar";
import { AnalyticsRankingMetric, DailyLiveStreamStatsByTitle } from "../../../types/Analytics";
import Ghost from "../../../components/Ghost";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useTitlesService from "../../../services/TitlesService";
import PositiveButton from "../../../components/PositiveButton";
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate, useSearchParams } from "react-router-dom";
import { Title } from "../../../types/Title";
import { ymd } from "../../../utilities/DateUtilities";
import useUserService from "../../../services/UserService";
import { PIE_COLORS } from "../../../utilities/GraphUtilities";

const minutesToHours = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = (minutes % 60).toFixed(0);

  return `${hours}h ${mins}m`;
};

export default function AdminAnalyticsTitlesPage() {

  const {
    getLiveAppRankings,
    getDailyLiveStreamForTitleStats
  } = useAnalyticsService();

  const [searchParams, setSearchParams] = useSearchParams();

  const { getTitle, searchTitles } = useTitlesService();
  const { getUser } = useUserService();

  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isLoadingGame, setIsLoadingGame] = useState(false);

  const [byHour, setByHour] = useState(false);
  const { startDate, endDate, setRange } = useUrlDateRange(7);
  const [gameTitle, setGameTitle] = useState<Title | null>(null);
  const [dailyGameStats, setDailyGameStats] = useState<DailyLiveStreamStatsByTitle | null>(null);
  const [gameId, setGameId] = useState<string | null>(null);
  const [rankings, setRankings] = useState<any[] | null>(null);
    

  const navigate = useNavigate();

  const clearGame = () => {
    setGameTitle(null);
    setSearchParams((prev) => {
      prev.delete("titleId");
      return prev;
    }, { replace: true });
  }

  const getSummaryData = async () => {
    if (isLoadingSummary) {
      return;
    }

    setIsLoadingSummary(true);

    await Promise.all([getAndProcessRankings(), getAndProcessGameData()]);

    setIsLoadingSummary(false);
  };

  const getAndProcessRankings = async () => {
    const data = await getLiveAppRankings(
      startDate!,
      endDate!,
      AnalyticsRankingMetric.MinutesStreamed,
      true,
      31
    );

    // Sort them by rankings.
    data.items.sort((a, b) => b.value - a.value);

    // Remove the "NoTitle" if it exists.
    var items = data.items
      .filter((item) => item.keyId !== "NoTitle")
      .map((item) => ({
        id: item.keyId,
        dateFriendlyName: item.keyName,
        minutesStreamed: item.value,
      }));

    setRankings(items);
  };

  const selectTitle = async (titleId: string) => {
    setSearchParams((prev) => ({
      startDate: prev.get("startDate") || "",
        endDate: prev.get("endDate") || "",
      titleId: titleId,
    }), { replace: true });
  };

  // Sometimes the name ends with a long number, remove that.
  const tidyName = (name: string) => {

    const rename = /^oculus_(.+)_(\d+)$/; // group 1 = username, group 2 = numeric id

    var tidy = name;

    // Get rid of the oculus prefix if it exists.
    const match = name.match(rename);
    if (match) {
      tidy = match[1];
    }

    // Sometimes the name also ends in a long number like BbyAxzul_25973933008857723. Remove the long number.
    const longNumberSuffix = /_(\d{5,})$/; // underscore followed by 5 or more digits
    tidy = tidy.replace(longNumberSuffix, "");

    return tidy;
  }

  const tidyDate = (date: Date) => {
    return ymd(date);
  };

  const getAndProcessGameData = async () => {
    if (!gameId) return;

    setIsLoadingGame(true);
    setDailyGameStats(null);
    
    const title = await getTitle(gameId);

    const gameStats = await getDailyLiveStreamForTitleStats(gameId, startDate!, endDate!);

    console.log("Game Stats:", gameStats);

    // Iterate through the users and tidy up their names.
    for (const userStat of gameStats.sumUsersStreams) {
      userStat.userName = tidyName(userStat.userName);
    }

    setGameTitle(title);
    setDailyGameStats(gameStats);

    setIsLoadingGame(false);
  };

  const handleStreamerClick = (streamer: any) => {
    // Handle the streamer click event
    navigate(`/admin/users/${streamer.userId}`);
  };

  useEffect(() => {
    if (!startDate || !endDate) return;

    getSummaryData();
  }, [startDate, endDate]);

  useEffect(() => {
    if (!searchParams) return;

    // Get the string value.
    var titleId = searchParams.get("titleId");

    if (!titleId) {
      setGameId(null);
      return;
    }

    setGameId(titleId);
  }, [searchParams]);

  useEffect(() => {
    if (!gameId) return;

    // Fetch and process game data when the game title changes
    getAndProcessGameData();
  }, [gameId]);

  return (
    <div className="grid grid-cols gap-4">
      {gameTitle && (
        <>
          <div className="flex flex-row">
            <h2 className="text-2xl">{gameTitle.name}</h2>
            <span className="flex-grow" />
            <PositiveButton onClick={() => clearGame()}>
              <ClearIcon /> Clear
            </PositiveButton>
          </div>
          <section>
            {startDate && endDate && (
              <DateRangeToolbar
                startDate={startDate}
                endDate={endDate}
                onChange={setRange}
              />
            )}
          </section>
          <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {isLoadingGame && (
              <>
                <Ghost additionalClasses="w-full min-h-64 md:min-h-80" />
                <Ghost additionalClasses="w-full min-h-64 md:min-h-80" />
              </>
            )}
            {!isLoadingGame && dailyGameStats && (
              <>
                <div className="hidden xl:block w-full h-64 lg:h-96 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyGameStats.dailyStreams}>
                      <Bar
                        name="Minutes Streamed"
                        dataKey="totalStreamMinutes"
                        fill="#60a5fa"
                      />
                      <Line
                        type="monotone"
                        dataKey="totalWatchMinutes"
                        name="Minutes watched"
                        stroke="#f59e0b" // amber-500
                        strokeWidth={2}
                        dot={false}
                      />
                      <Legend />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <YAxis
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <Tooltip
                        labelStyle={{ color: "#000" }} // date/header
                        itemStyle={{ color: "#000" }} // series/value rows
                        contentStyle={{
                          backgroundColor: "#fff",
                          borderColor: "#e5e7eb",
                        }} // optional
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-auto h-auto  lg:h-96 md:overflow-y-auto">
                  <table className="table-auto w-full">
                    <thead>
                      <tr className="">
                        <th className="font-normal text-left p-2">Date</th>
                        <th className="font-normal text-left p-2">
                          Minutes Watched
                        </th>
                        <th className="font-normal text-left p-2 hidden md:table-cell">
                          Minutes Streamed
                        </th>
                        <th className="font-normal text-left p-2 hidden md:table-cell">
                          Streams
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyGameStats.dailyStreams.map((item) => (
                        <tr
                          key={item.date.toString()}
                          className="border-t border-cyan-900"
                        >
                          <td className="p-2">{item.date.toString()}</td>
                          <td className="p-2">
                            {minutesToHours(item.totalWatchMinutes)}
                          </td>
                          <td className="p-2 hidden md:table-cell">
                            {minutesToHours(item.totalStreamMinutes)}
                          </td>
                          <td className="p-2 hidden md:table-cell">
                            {item.streamCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
          {(!dailyGameStats || !dailyGameStats.sumUsersStreams || dailyGameStats.sumUsersStreams.length === 0) && (
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Ghost additionalClasses="w-full min-h-64 md:min-h-80" />
              <Ghost additionalClasses="w-full min-h-64 md:min-h-80" />
            </section>
          )}
          {dailyGameStats && dailyGameStats.sumUsersStreams && dailyGameStats.sumUsersStreams.length > 0 && (
            <section className="grid grid-cols gap-4">
              <h3 className="text-xl mt-4">Top 10 Streamers</h3>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="hidden xl:block w-full h-64 lg:h-96 min-w-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                    {/* Minutes Streamed */}
                    <div className="h-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <text
                            x="50%"
                            y={18}
                            textAnchor="middle"
                            fill="#fff"
                            fontSize={14}
                          >
                            Minutes Streamed
                          </text>
                          <Pie
                            data={dailyGameStats.sumUsersStreams}
                            dataKey="totalStreamMinutes"
                            nameKey="userName"
                            outerRadius="80%"
                            labelLine={false}
                            label={({ name }) => name}
                            onClick={(d) => handleStreamerClick(d.page)}
                          >
                            {dailyGameStats.sumUsersStreams.map((s, i) => (
                              <Cell
                                key={s.userId}
                                fill={PIE_COLORS[i % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(
                              value: any,
                              _n: any,
                              { payload }: any
                            ) => [
                              minutesToHours(Number(value)),
                              payload.userName || payload.userId,
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Minutes Viewed */}
                    <div className="h-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <text
                            x="50%"
                            y={18}
                            textAnchor="middle"
                            fill="#fff"
                            fontSize={14}
                          >
                            Minutes Viewed
                          </text>
                          <Pie
                            data={dailyGameStats.sumUsersStreams}
                            dataKey="totalWatchMinutes"
                            nameKey="userName"
                            outerRadius="80%"
                            labelLine={false}
                            label={({ name }) => name}
                            onClick={(d) => handleStreamerClick(d.payload)}
                          >
                            {dailyGameStats.sumUsersStreams.map((s, i) => (
                              <Cell
                                key={s.userId}
                                fill={PIE_COLORS[i % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(
                              value: any,
                              _n: any,
                              { payload }: any
                            ) => [
                              minutesToHours(Number(value)),
                              payload.userName || payload.userId,
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div>
                  <table className="table-auto w-full">
                    <thead>
                      <tr className="">
                        <th className="font-normal text-left p-2">Streamer</th>
                        <th className="font-normal text-left p-2">
                          Minutes Watched
                        </th>
                        <th className="font-normal text-left p-2 hidden md:table-cell">
                          Minutes Streamed
                        </th>
                        <th className="font-normal text-left p-2 hidden md:table-cell">
                          Streams
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyGameStats.sumUsersStreams.map((item) => (
                        <tr
                          onClick={() => handleStreamerClick(item)}
                          key={item.userId}
                          className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                        >
                          <td className="p-2">
                            {item.userName || item.userId}
                          </td>
                          <td className="p-2">
                            {minutesToHours(item.totalWatchMinutes)}
                          </td>
                          <td className="p-2 hidden md:table-cell">
                            {minutesToHours(item.totalStreamMinutes)}
                          </td>
                          <td className="p-2 hidden md:table-cell">
                            {item.streamCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}
        </>
      )}
      {!gameTitle && !isLoadingGame && (
        <>
          <h2 className="text-2xl">Titles</h2>
          <section>
            {startDate && endDate && (
              <DateRangeToolbar
                startDate={startDate}
                endDate={endDate}
                onChange={setRange}
              />
            )}
          </section>

          <section className="grid grid-cols gap-4">
            <h3 className="text-xl">Top Titles</h3>
            <div className="w-full h-64 md:h-80 min-w-0">
              {isLoadingSummary && <Ghost additionalClasses="w-full h-full" />}
              {!isLoadingSummary && rankings && (
                <div className="w-full grid grid-rows gap-4 md:h-full">
                  <div className="hidden md:block h-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={rankings}>
                        <Bar
                          name="Minutes Streamed"
                          dataKey="minutesStreamed"
                          fill="#60a5fa"
                          onClick={(item, index, event) => {
                            selectTitle(item.id!);
                          }}
                        />
                        <Legend />
                        <XAxis
                          dataKey="dateFriendlyName"
                          tick={{ fill: "#fff" }}
                          axisLine={{ stroke: "#fff" }}
                          tickLine={{ stroke: "#fff" }}
                        />
                        <YAxis
                          tick={{ fill: "#fff" }}
                          axisLine={{ stroke: "#fff" }}
                          tickLine={{ stroke: "#fff" }}
                        />
                        <Tooltip
                          labelStyle={{ color: "#000" }} // date/header
                          itemStyle={{ color: "#000" }} // series/value rows
                          contentStyle={{
                            backgroundColor: "#fff",
                            borderColor: "#e5e7eb",
                          }} // optional
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="md:hidden">
                    <table className="table-auto w-full">
                      <thead>
                        <tr className="">
                          <th className="text-left p-2">Title</th>
                          <th className="text-left p-2">Minutes Streamed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankings.map((item) => (
                          <tr
                            key={item.id}
                            onClick={() => selectTitle(item.id)}
                            className="border-t border-cyan-900 hover:bg-cyan-300/25 cursor-pointer"
                          >
                            <td className="p-2">{item.dateFriendlyName}</td>
                            <td className="p-2">
                              {minutesToHours(item.minutesStreamed.toFixed(0))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            {rankings && (
              <div className="hidden md:grid grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {rankings.map((item) => (
                  <button
                    key={item.id}
                    className="flex justify-between p-2 card hover:bg-cyan-300/25 cursor-pointer"
                    onClick={() => selectTitle(item.id)}
                  >
                    <span>{item.dateFriendlyName}</span>
                    <span>
                      {minutesToHours(item.minutesStreamed.toFixed(0))}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
